import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {MenuMode, MenuService} from '../../shared/menu.service';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {catchError, first, map, switchMap, takeUntil} from 'rxjs/operators';
import {Router} from '@angular/router';
import {isAdvancedUser, isDefined} from '../../commons/commons';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {EntityType} from 'smag-commons/cfg/hierarchy';
import {BooleanResponse, EntityNetworkType} from 'smag-commons/dto/api';
import {SmagApiService} from '../../api/smag-api.service';
import {AuthService} from 'auth-commons';

enum MENU_TITLES {
    DASHBOARD = 'Dashboard',
    MEASURES = 'Measures',
    ECONOMICS = 'Economics',
    SUBJECTS = 'Subjects',
    RESOURCES = 'Resources',
    NOTIFICATIONS = 'Notifications'
}

interface MenuEntry {
    name: MENU_TITLES;
    url: string[];
    icon: string;
    queryParamsFactory?: () => Promise<{ [p: string]: any }>;
}

const DEV_DASHBOARD_URL = ['/c', 'dev-dashboard'];

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();

    @Input() collapsed: boolean; // true by default

    entries: MenuEntry[];
    mode: MenuMode | null;
    ecResource: HierarchyEntity | null; // currently selected energy community (if relevant at all)
    isAdvUser: Observable<BooleanResponse>;

    // exported in HTML
    MenuMode = MenuMode;
    MENU_TITLES = MENU_TITLES;

    constructor(private readonly menuService: MenuService,
                private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService,
                private readonly router: Router,
                private readonly authService: AuthService) {
        this.entries = [];
        this.mode = null;
        this.ecResource = null;
    }

    ngOnInit() {
        this.menuService.menuMode.pipe(
            takeUntil(this.destroyed)
        ).subscribe(mode => {
            this.mode = mode;
            this.entries = this.mkMenuEntries(mode);
        });

        combineLatest([this.menuService.menuMode, this.hierarchyService.selection]).pipe(
            switchMap(([mode, resource]) => mode === MenuMode.ENERGY_COMMUNITY ?
                ((!resource || resource.type === EntityType.ENERGY_COMMUNITY) ?
                    of(resource) : // either no resource or an energy community is selected
                    this.getResourceCommunity(resource.id)) :
                of(null as HierarchyEntity)), // something that *could* be part of an energy community is selected
            takeUntil(this.destroyed)
        ).subscribe(ecResource => {
            this.ecResource = ecResource;
        });

        this.isAdvUser = isAdvancedUser(of(this.authService.profile)).pipe(map(resp => ({value: resp})));
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get isCollapsed(): boolean {
        return !isDefined(this.collapsed) || this.collapsed;
    }

    trackByIndex(index: number, _: MenuEntry): number {
        return index;
    }

    isActive(entry: MenuEntry): boolean {
        return this.router.isActive(this.router.createUrlTree(entry.url), false);
    }

    onClickEntry(entry: MenuEntry) {
        if (entry.queryParamsFactory) {
            entry.queryParamsFactory()
                .then(params => this.router.navigate(entry.url, {
                    queryParams: params || {},
                    queryParamsHandling: 'merge'
                }));
        } else {
            this.router.navigate(entry.url, {
                queryParams: {},
                queryParamsHandling: 'merge'
            }).then();
        }
    }

    onClickDevDashboard() {
        this.router.navigate(DEV_DASHBOARD_URL).then();
    }

    private mkMenuEntries(mode: MenuMode): MenuEntry[] {
        switch (mode) {
            case MenuMode.ENERGY_COMMUNITY:
                return [
                    {
                        name: MENU_TITLES.DASHBOARD,
                        url: ['/c', 'ec', 'dashboard'],
                        icon: 'fal-home',
                        queryParamsFactory: this.mkECQueryParamsFactory(true),
                    },
                    {
                        name: MENU_TITLES.MEASURES,
                        url: ['/c', 'ec', 'measures'],
                        icon: 'fal-tachometer-alt',
                        queryParamsFactory: this.mkECQueryParamsFactory(false),
                    },
                    {
                        name: MENU_TITLES.ECONOMICS,
                        url: ['/c', 'ec', 'economics'],
                        icon: 'fal-euro-sign',
                        queryParamsFactory: this.mkECQueryParamsFactory(true),
                    },
                    {
                        name: MENU_TITLES.SUBJECTS,
                        url: ['/c', 'ec', 'members'],
                        icon: 'fal-user-friends',
                        queryParamsFactory: this.mkECQueryParamsFactory(true),
                    },
                    {
                        name: MENU_TITLES.RESOURCES,
                        url: ['/c', 'ec', 'resources'],
                        icon: 'fal-sitemap',
                        queryParamsFactory: this.mkECQueryParamsFactory(false),
                    },
                    {
                        name: MENU_TITLES.NOTIFICATIONS,
                        url: ['/c', 'ec', 'notifications'],
                        icon: 'fal-bell',
                        queryParamsFactory: this.mkECQueryParamsFactory(true),
                    }
                ];
            default:
                return [];
        }
    }

    private getResourceCommunity(resourceId: string): Observable<HierarchyEntity | null> {
        return this.apiService.getNetworkOfResource(resourceId).pipe(
            switchMap(networkInfo => networkInfo?.type === EntityNetworkType.ENERGY_COMMUNITY ?
                this.hierarchyService.findEntityById(networkInfo.network_id).pipe(first()) :
                of(null)),
            catchError(() => of(null))
        );
    }

    private mkECQueryParamsFactory(forceEC: boolean): () => Promise<{ [p: string]: any }> {
        return () => {
            if (forceEC) {
                const params: { [p: string]: any } = {};
                if (this.ecResource) {
                    params.id = this.ecResource.id;
                }
                return Promise.resolve(params);
            } else {
                return Promise.resolve({}); // leave everything as it is
            }
        };
    }
}
